import React, { useState, useEffect } from 'react';
import {
Table,
TableBody,
TableCell,
TableContainer,
TableHead,
TableRow,
Paper,
TextField,
Select,
MenuItem,
Button,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
IconButton,
Tooltip,
Grid,
Typography,
Pagination,
Slide,
Fade,
InputAdornment,
Snackbar,
Alert,
Badge,
Popover,
FormControlLabel,
Checkbox,
Box,
Avatar,
Chip
} from '@mui/material';
import {
Add as AddIcon,
Edit as EditIcon,
Delete as DeleteIcon,
Visibility as VisibilityIcon,
Search as SearchIcon,
Refresh as RefreshIcon,
Lock,
FilterList as FilterIcon,
Email as EmailIcon,
Person as PersonIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { TEXT_CONNECTION_ERROR } from '../utilities/Text';
import {
APIUserDelete,
APIUserList,
APIUserPassword,
APIUserStore,
APIUserUpdate,
headers
} from '../utilities/APIs';
import useCustomToast from '../hooks/useToast';
import axios from 'axios';
import { formatCreatedAtDate, formatNumberWithComma } from '../utilities/Functions';

const Users = () => {
const theme = useTheme();
const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

// State
const [loading, setLoading] = useState(true);
const [openAdd, setOpenAdd] = useState(false);
const [openEdit, setOpenEdit] = useState(false);
const [openPasswordEdit, setOpenPasswordEdit] = useState(false);
const [openView, setOpenView] = useState(false);
const [openDelete, setOpenDelete] = useState(false);
const [currentUser, setCurrentUser] = useState(null);
const [page, setPage] = useState(1);
const [rowsPerPage, setRowsPerPage] = useState(10);
const [searchTerm, setSearchTerm] = useState('');
const [error, setError] = useState(null);
const [postStatus, setPostStatus] = useState('ADD');
const [updateId, setUpdateId] = useState('');
const showToast = useCustomToast();
const [pageState, setPageState] = useState({
    pageData: []
});
const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
});

// Filter state
const [filterAnchorEl, setFilterAnchorEl] = useState(null);
const [selectedRoles, setSelectedRoles] = useState([]);
const [activeFilters, setActiveFilters] = useState(0);

// Form data
const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    password: ''
});

// Extract unique roles for filter
const uniqueRoles = [...new Set(pageState.pageData.map((user) => user.role))];

useEffect(() => {
    fetchData();
}, []);

const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
    const response = await axios.get(APIUserList, { headers });
    const data = Array.isArray(response.data.data) ? response.data.data.reverse() : [];

    setPageState((prev) => ({
        ...prev,
        pageData: data
    }));
    } catch (error) {
    let errorMessage = 'Failed to load data';

    if (error.response) {
        errorMessage = error.response.data.message || error.message;
    } else if (error.code === 'ERR_NETWORK') {
        errorMessage = TEXT_CONNECTION_ERROR;
    }

    setError(errorMessage);
    console.log(errorMessage);
    showToast('Error', errorMessage, 'error');
    } finally {
    setLoading(false);
    }
};

// Filter users based on search term and selected roles
const filteredUsers = pageState.pageData.filter((user) => {
    const matchesSearch =
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole = selectedRoles.length === 0 || selectedRoles.includes(user.role);

    return matchesSearch && matchesRole;
});

// Calculate active filter count
useEffect(() => {
    let count = 0;
    if (selectedRoles.length > 0) count += 1;
    if (searchTerm) count += 1;
    setActiveFilters(count);
}, [selectedRoles, searchTerm]);

// Pagination
const count = Math.ceil(filteredUsers.length / rowsPerPage);
const indexOfLastRow = page * rowsPerPage;
const indexOfFirstRow = indexOfLastRow - rowsPerPage;
const currentRows = filteredUsers.slice(indexOfFirstRow, indexOfLastRow);

const handleChangePage = (event, newPage) => {
    setPage(newPage);
};

const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
};

// Form handling
const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
    ...prev,
    [name]: value
    }));
};

// Filter handlers
const handleOpenFilter = (event) => {
    setFilterAnchorEl(event.currentTarget);
};

const handleCloseFilter = () => {
    setFilterAnchorEl(null);
};

const handleRoleFilterChange = (role) => {
    setSelectedRoles((prev) =>
    prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]
    );
    setPage(1);
};

const clearAllFilters = () => {
    setSelectedRoles([]);
    setSearchTerm('');
    setPage(1);
};

// Dialog handlers
const handleOpenAdd = () => {
    setPostStatus('ADD');
    setFormData({
    name: '',
    email: '',
    role: '',
    password: ''
    });
    setOpenAdd(true);
};

const handleOpenPasswordEdit = (user) => {
    setUpdateId(user.id);
    setPostStatus('PASSWORD');
    setOpenPasswordEdit(true);
};

const handleOpenEdit = (user) => {
    setFormData({
    name: user.name,
    email: user.email,
    role: user.role,
    password: ''
    });
    setCurrentUser(user);
    setUpdateId(user.id);
    setPostStatus('UPDATE');
    setOpenEdit(true);
};

const handleOpenView = (user) => {
    setCurrentUser(user);
    setOpenView(true);
};

const handleOpenDelete = (user) => {
    setCurrentUser(user);
    setUpdateId(user.id);
    setPostStatus('DELETE');
    setOpenDelete(true);
};

// Prevent modal close when backdrop (outside) is clicked
const handleClose = (event, reason) => {
    if (reason === 'backdropClick') return;

    setOpenAdd(false);
    setOpenEdit(false);
    setOpenView(false);
    setOpenDelete(false);
    setOpenPasswordEdit(false);
};

const handleRefresh = () => {
    setLoading(true);
    fetchData();
    setSnackbar({ open: true, message: 'Data refreshed', severity: 'info' });
};

const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
};

const validateForm = () => {
    const errors = [];
    if (!formData.name.trim()) errors.push('Name is required');
    if (!formData.email.trim()) errors.push('Email is required');
    if (!formData.role.trim()) errors.push('Role is required');

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
    errors.push('Please enter a valid email address');
    }

    if (errors.length > 0) {
    showToast('Validation Error', errors.join('\n'), 'warning');
    return false;
    }
    return true;
};

const handleSubmit = async (e) => {
    e.preventDefault();
    if (postStatus !== 'DELETE' && postStatus !== 'PASSWORD') {
    if (!validateForm()) return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('name', formData.name);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('role', formData.role);

    if (formData.password) {
    formDataToSend.append('password', formData.password);
    }

    formDataToSend.append('updateId', updateId);

    try {
    let postRequest;
    if (postStatus === 'ADD') {
        postRequest = await axios.post(APIUserStore, formDataToSend, { headers });
    } else if (postStatus === 'UPDATE') {
        postRequest = await axios.post(APIUserUpdate, formDataToSend, { headers });
    } else if (postStatus === 'DELETE') {
        postRequest = await axios.post(APIUserDelete, formDataToSend, { headers });
    } else if (postStatus === 'PASSWORD') {
        const passwordFormData = new FormData();
        passwordFormData.append('password', formData.password);
        passwordFormData.append('updateId', updateId);
        postRequest = await axios.post(APIUserPassword, passwordFormData, { headers });
    }

    const response = postRequest.data.status;
    const message = postRequest.data.message;

    if (response === 'success') {
        handleClose();
        fetchData();
        showToast('Success', message, 'success');
    } else {
        showToast('Error', 'Unknown Server Error, Record Not Created', 'error');
    }
    } catch (error) {
    if (error.response) {
        if (error.response.status === 422) {
        const errors = error.response.data.errors;
        let errorMessages = [];

        for (const field in errors) {
            errorMessages.push(...errors[field]);
        }

        showToast('Validation Error', errorMessages.join('\n'), 'error');
        } else {
        showToast('Error', error.response.data.message || 'An error occurred', 'error');
        }
    } else if (error.code === 'ERR_NETWORK') {
        showToast('Connection Error', TEXT_CONNECTION_ERROR, 'error');
    } else {
        showToast('Error', 'An unexpected error occurred', 'error');
    }
    }
};

// Get user initials for avatar
const getUserInitials = (name) => {
    return name
    .split(' ')
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

if (loading) return <div>Loading...</div>;
if (error) return showToast('Error', error, 'error');

return (
    <div style={{ marginTop: 15, padding: isMobile ? '8px' : '24px' }}>
    <Slide direction="down" in={true} mountOnEnter unmountOnExit>
        <Paper
        elevation={3}
        style={{
            padding: isMobile ? '16px' : '24px',
            borderRadius: '12px',
            marginBottom: '24px'
        }}
        >
        <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
            <Grid item>
            <Badge
                badgeContent={formatNumberWithComma(pageState.pageData.length)}
                max={formatNumberWithComma(pageState.pageData.length)}
                color="success"
            >
                <Typography variant="subtitle1">Users Total</Typography>
            </Badge>
            </Grid>
            <Grid item>
            <Button
                variant="contained"
                color="warning"
                startIcon={<AddIcon />}
                onClick={handleOpenAdd}
                style={{ borderRadius: '8px' }}
            >
                Add User
            </Button>
            </Grid>
        </Grid>

        <Grid container spacing={2} style={{ marginTop: '8px' }}>
            <Grid item xs={12} sm={6} md={4}>
            <TextField
                fullWidth
                variant="outlined"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
                }}
                InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                    <SearchIcon />
                    </InputAdornment>
                ),
                style: { borderRadius: '8px' }
                }}
            />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
            <Select
                fullWidth
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                variant="outlined"
                style={{ borderRadius: '8px' }}
            >
                <MenuItem value={5}>5 per page</MenuItem>
                <MenuItem value={10}>10 per page</MenuItem>
                <MenuItem value={25}>25 per page</MenuItem>
                <MenuItem value={50}>50 per page</MenuItem>
            </Select>
            </Grid>
            <Grid item xs={12} md={4} style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Tooltip title="Filter users">
                <Badge badgeContent={activeFilters} color="error">
                <Button
                    variant="contained"
                    startIcon={<FilterIcon />}
                    onClick={handleOpenFilter}
                    style={{ borderRadius: '8px' }}
                >
                    Filter Options
                </Button>
                </Badge>
            </Tooltip>
            <Tooltip title="Refresh data">
                <IconButton
                onClick={handleRefresh}
                style={{ backgroundColor: theme.palette.grey[200], borderRadius: '8px' }}
                >
                <RefreshIcon />
                </IconButton>
            </Tooltip>
            </Grid>
        </Grid>
        </Paper>
    </Slide>

    {/* Filter Popover */}
    <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleCloseFilter}
        anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left'
        }}
        transformOrigin={{
        vertical: 'top',
        horizontal: 'left'
        }}
    >
        <Box sx={{ p: 2, minWidth: 200 }}>
        <Typography variant="h6" gutterBottom>
            Filters
        </Typography>

        <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            Role
        </Typography>
        {uniqueRoles.map((role) => (
            <FormControlLabel
            key={role}
            control={
                <Checkbox
                checked={selectedRoles.includes(role)}
                onChange={() => handleRoleFilterChange(role)}
                />
            }
            label={role}
            />
        ))}

        <Box sx={{ mt: 2 }}>
            <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={clearAllFilters}
            >
            Clear All Filters
            </Button>
        </Box>
        </Box>
    </Popover>

    <Fade in={!loading} timeout={500}>
        <div>
        <TableContainer component={Paper} elevation={3} style={{ borderRadius: '12px', marginBottom: '24px' }}>
            <Table>
            <TableHead>
                <TableRow style={{ backgroundColor: theme.palette.primary.main }}>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>#</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>User</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Role</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Created Date</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {currentRows.length > 0 ? (
                currentRows.map((user, index) => (
                    <StyledTableRow key={user.id}>
                    <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                            {getUserInitials(user.name)}
                        </Avatar>
                        <Typography variant="body2" fontWeight="medium">
                            {user.name}
                        </Typography>
                        </Box>
                    </TableCell>
                    <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <EmailIcon color="action" fontSize="small" />
                        <Typography variant="body2">{user.email}</Typography>
                        </Box>
                    </TableCell>
                    <TableCell>
                        <Chip
                        label={user.role}
                        color={user.role === 'Admin' ? 'primary' : 'default'}
                        size="small"
                        variant="outlined"
                        />
                    </TableCell>
                    <TableCell>
                        <Typography variant="body2">
                        {formatCreatedAtDate(user.created_at)}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        <Tooltip title="View details">
                            <ActionButton
                            variant="contained"
                            color="info"
                            size="small"
                            onClick={() => handleOpenView(user)}
                            style={{ backgroundColor: '#17a2b8' }}
                            >
                            <VisibilityIcon fontSize="small" />
                            </ActionButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                            <ActionButton
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => handleOpenEdit(user)}
                            >
                            <EditIcon fontSize="small" />
                            </ActionButton>
                        </Tooltip>
                        <Tooltip title="Change Password">
                            <ActionButton
                            variant="contained"
                            color="secondary"
                            size="small"
                            onClick={() => handleOpenPasswordEdit(user)}
                            >
                            <Lock fontSize="small" />
                            </ActionButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                            <ActionButton
                            variant="contained"
                            color="error"
                            size="small"
                            onClick={() => handleOpenDelete(user)}
                            >
                            <DeleteIcon fontSize="small" />
                            </ActionButton>
                        </Tooltip>
                        </div>
                    </TableCell>
                    </StyledTableRow>
                ))
                ) : (
                <TableRow>
                    <TableCell colSpan={6} style={{ textAlign: 'center', padding: '40px' }}>
                    <Typography variant="subtitle1">
                        {searchTerm || selectedRoles.length > 0
                        ? 'No matching users found'
                        : 'No user records available'}
                    </Typography>
                    </TableCell>
                </TableRow>
                )}
            </TableBody>
            </Table>
        </TableContainer>

        {filteredUsers.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
            <Pagination
                count={count}
                page={page}
                onChange={handleChangePage}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
            />
            </div>
        )}
        </div>
    </Fade>

    {/* Add User Dialog */}
    <Dialog
        open={openAdd}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Slide}
        transitionDuration={300}
    >
        <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>
            Add New User
        </Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px' }}>
        <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
            <Grid item xs={12}>
                <TextField
                fullWidth
                label="Full Name"
                name="name"
                value={formData.name}
                autoComplete="off"
                onChange={handleInputChange}
                required
                variant="outlined"
                helperText="Enter user's full name"
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                autoComplete="off"
                onChange={handleInputChange}
                required
                variant="outlined"
                helperText="Enter valid email address"
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                fullWidth
                select
                label="Role"
                name="role"
                value={formData.role}
                autoComplete="off"
                onChange={handleInputChange}
                required
                variant="outlined"
                >
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Staff">Staff</MenuItem>
                <MenuItem value="User">User</MenuItem>
                </TextField>
            </Grid>
            <Grid item xs={12}>
                <TextField
                fullWidth
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                autoComplete="new-password"
                onChange={handleInputChange}
                required
                variant="outlined"
                helperText="Set initial password for the user"
                />
            </Grid>
            </Grid>
        </form>
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: '16px 24px' }}>
        <Button onClick={() => handleClose(null, 'closeButton')} color="error" variant="outlined">
            Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">
            Save User
        </Button>
        </DialogActions>
    </Dialog>

    {/* Edit User Dialog */}
    <Dialog
        open={openEdit}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Slide}
        transitionDuration={300}
    >
        <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>
            Edit User
        </Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px' }}>
        <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
            <Grid item xs={12}>
                <TextField
                fullWidth
                label="Full Name"
                name="name"
                value={formData.name}
                autoComplete="off"
                onChange={handleInputChange}
                required
                variant="outlined"
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                autoComplete="off"
                onChange={handleInputChange}
                required
                variant="outlined"
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                fullWidth
                select
                label="Role"
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                autoComplete="off"
                required
                variant="outlined"
                >
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Staff">Staff</MenuItem>
                <MenuItem value="User">User</MenuItem>
                </TextField>
            </Grid>
            </Grid>
        </form>
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: '16px 24px' }}>
        <Button onClick={() => handleClose(null, 'closeButton')} color="error" variant="outlined">
            Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">
            Update User
        </Button>
        </DialogActions>
    </Dialog>

    {/* Password Edit Dialog */}
    <Dialog
        open={openPasswordEdit}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Slide}
        transitionDuration={300}
    >
        <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>
            Update User Password
        </Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px' }}>
        <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
            <Grid item xs={12}>
                <TextField
                fullWidth
                label="New Password"
                name="password"
                type="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleInputChange}
                required
                variant="outlined"
                helperText="Enter new password for the user"
                />
            </Grid>
            </Grid>
        </form>
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: '16px 24px' }}>
        <Button onClick={() => handleClose(null, 'closeButton')} color="error" variant="outlined">
            Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">
            Update Password
        </Button>
        </DialogActions>
    </Dialog>

    {/* View User Dialog */}
    <Dialog
        open={openView}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Slide}
        transitionDuration={300}
    >
        <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>
            User Details
        </Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px' }}>
        {currentUser && (
            <Grid container spacing={3}>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Avatar
                sx={{
                    bgcolor: theme.palette.primary.main,
                    width: 64,
                    height: 64,
                    fontSize: '1.5rem'
                }}
                >
                {getUserInitials(currentUser.name)}
                </Avatar>
            </Grid>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PersonIcon color="action" />
                <Typography variant="subtitle1" fontWeight="bold">
                    Name:
                </Typography>
                </Box>
                <Typography variant="body1" sx={{ ml: 4 }}>
                {currentUser.name}
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <EmailIcon color="action" />
                <Typography variant="subtitle1" fontWeight="bold">
                    Email:
                </Typography>
                </Box>
                <Typography variant="body1" sx={{ ml: 4 }}>
                {currentUser.email}
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PersonIcon color="action" />
                <Typography variant="subtitle1" fontWeight="bold">
                    Role:
                </Typography>
                </Box>
                <Chip
                label={currentUser.role}
                color={currentUser.role === 'Admin' ? 'primary' : 'default'}
                sx={{ ml: 4 }}
                />
            </Grid>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PersonIcon color="action" />
                <Typography variant="subtitle1" fontWeight="bold">
                    Created Date:
                </Typography>
                </Box>
                <Typography variant="body1" sx={{ ml: 4 }}>
                {formatCreatedAtDate(currentUser.created_at)}
                </Typography>
            </Grid>
            </Grid>
        )}
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: '16px 24px' }}>
        <Button onClick={() => handleClose(null, 'closeButton')} color="primary" variant="contained">
            Close
        </Button>
        </DialogActions>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog
        open={openDelete}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Slide}
        transitionDuration={300}
    >
        <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>
            Confirm Delete
        </Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px', textAlign: 'center' }}>
        <Typography variant="body1" style={{ marginBottom: '16px' }}>
            Are you sure you want to delete this user?
        </Typography>
        <Typography variant="h6" color="error" style={{ fontWeight: 600 }}>
            {currentUser?.name}
        </Typography>
        <Typography variant="caption" display="block" style={{ marginTop: '8px' }}>
            This action cannot be undone.
        </Typography>
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: '16px 24px' }}>
        <Button onClick={() => handleClose(null, 'closeButton')} variant="outlined" color="primary">
            Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="error" startIcon={<DeleteIcon />}>
            Delete
        </Button>
        </DialogActions>
    </Dialog>

    {/* Snackbar for notifications */}
    <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
    >
        <Alert
        onClose={handleCloseSnackbar}
        severity={snackbar.severity}
        variant="filled"
        sx={{ width: '100%' }}
        >
        {snackbar.message}
        </Alert>
    </Snackbar>
    </div>
);
};

export default Users;

// Styled components
const StyledTableRow = styled(TableRow)(({ theme }) => ({
'&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover
},
'&:hover': {
    backgroundColor: theme.palette.action.selected,
    transition: 'background-color 0.3s ease'
}
}));

const ActionButton = styled(Button)(({ theme }) => ({
margin: theme.spacing(0.5),
minWidth: 'auto'
}));